import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";

// components
import { Box, Button, Typography, Divider } from "@material-ui/core";
import {
  IContactInfo,
  TravelerSelectStep,
  HotelNeedToKnowPanel,
  ContactInfoForm,
  DesktopPopupModal,
  RoomDetails,
  CheckoutStepper,
  ButtonWrap,
  emailRegex,
  phoneRegex,
  ActionButton,
  ActionLink,
  IconName,
  Icon,
  InformationalModal as TreesModal,
  VoidWindowNotice,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
  SanitizedHtml,
} from "halifax";
import { RouteComponentProps } from "react-router";
import {
  ADD_CONTACT_INFO,
  CallState,
  CheckInPolicy,
  HOTEL_SAVE_AND_CONTINUE_CLICKED,
  getPromotionTrackingProperties,
  ITravelerStepErrors,
  HotelBookType,
  LodgingCollectionEnum,
  CancellationReason,
  CancellationPolicyEnum,
  CoreStaysCheckout,
} from "redmond";
import queryStringParser from "query-string";
import clsx from "clsx";
import { DesktopShop, HotelShopProgressBar } from "../../../../shop/components";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import { ReshopSuccessBanner } from "../../ReshopSuccessBanner";
import { ConnectionRetrySuccessBanner } from "../../ConnectionRetrySuccessBanner";
import { PriceChangeInlineMessage } from "../../PriceChangeInlineMessage";
import {
  PriceBreakdown,
  HotelBookPassengerSelection,
  PaymentCard,
  HotelBookSummaryPanel,
  AGENT_FEE,
} from "../..";
import { getCancellationPolicyInfoFromQuote } from "../../../utils";

// types, constants
import { DesktopHotelBookWorkflowConnectorProps } from "./container";
import {
  BOOK_BUTTON_TEXT,
  CONTACT_INFO_SUBTITLE,
  CONTACT_INFO_TITLE_NO_STEP,
  CONTACT_INFO_HELPER_TEXT,
  NEED_TO_KNOW_TITLE,
  NEED_TO_KNOW_DETAILS,
  REQUIRED_DETAILS_TEXT,
  getCfarSecondaryText,
  TRAVELER_INFO_TITLE,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_SUBTITLE,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  AGENT_DETAILS_MODAL_TEXT,
  CHECK_IN_INSTRUCTIONS_TITLE,
  formattedCheckIn,
  formattedCheckOut,
  CALIFORNIA_BILL_644_CANCELLATION_TEXT,
} from "./textConstants";

// context
import { ClientContext } from "../../../../../App";
import {
  AVAILABLE,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../../context/experiments";

// helpers, utils
import { fetchCustomerDetails } from "../../../../../api/v0/customer/fetchCustomerDetails";
import { PATH_BOOK_CONFIRMATION, PATH_HOME } from "../../../../../utils/paths";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";

// styles
import "./styles.scss";
import PremierCollectionCheckoutBenefits from "../../PremierCollectionCheckoutBenefits";
import { CfarCancellationPolicy } from "../../../../ancillary/components/addOnComponents/cfar/CfarCancellationPolicy";
import { CDN_ASSETS, getConfigTenant, isVesperTenant } from "@capone/common";
import dayjs from "dayjs";

export interface IDesktopHotelBookWorkflowProps
  extends RouteComponentProps,
    DesktopHotelBookWorkflowConnectorProps {
  core?: CoreStaysCheckout;
}

export const DesktopHotelBookWorkflow = ({
  setContactInfo,
  priceQuote,
  priceQuoteCallState,
  schedulePriceQuote,
  chosenProduct,
  selectedLodging,
  roomInfoProduct,
  chosenProductIndex,
  combinedBookingSteps,
  reservation,
  history,
  isBookingValid,
  email,
  phoneNumber,
  allStepsCompleted,
  priceDifferenceAcknowledged,
  scheduleBook,
  isTravelerStepComplete,
  isContactStepComplete,
  setSelectedPaymentMethodId,
  setSelectedRewardsAccountReferenceId,
  priceQuoteErrors,
  confirmationDetailsErrors,
  setPriceQuote,
  roomsCount,
  isTravelWalletPaymentOnly,
  isCustomizePageEnabled,
  ancillaries,
  hasCfarAttached,
  hotelBookType,
  isReadyToDisplayHotelSummaryDetails,
  checkIn,
  checkOut,
  cancellationSummary,
  offerToApply,
  showRequoteSuccessBanner,
  core,
}: IDesktopHotelBookWorkflowProps) => {
  const expState = useExperiments();

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment],
  );

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT,
  );
  const isVesper = isVesperTenant(getConfigTenant());
  const isTravelWalletOfferExperiment = useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  );
  const isCaliforniaBill644Experiment = useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant],
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const clientContext = useContext(ClientContext);
  const { sessionInfo, isAgentPortal } = clientContext;
  const isFirstStepCompletedInCheckout =
    isContactStepComplete && isTravelerStepComplete;

  const [saveButtonClicked, setSaveButtonClicked] = useState(false);
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
  });
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // TODO: remove this when we decide if using text message - https://hopchat.slack.com/archives/C01FX0M22MV/p1620146159000600
  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: phoneNumber || "",
    email: email || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    useState<boolean>(true);
  const [disableEditTravelerInfo, setDisableEditTravelerInfo] = useState(false);
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [treeModalOpen, setTreeModalOpen] = useState(false);

  const showCheckoutBreadcrumbs =
    hotelBookType === HotelBookType.DEFAULT && isCustomizePageEnabled;
  const showCheckoutBreadcrumbsCustomizeStep =
    hotelBookType === HotelBookType.DEFAULT && isCustomizePageEnabled;

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const cancellationPolicyInfo = getCancellationPolicyInfoFromQuote({
    quote: priceQuote,
    product: chosenProduct,
    getCfarSecondaryText,
  });

  const onBookHotel = () => {
    const queryString = queryStringParser.parse(history.location.search);
    priceDifferenceAcknowledged || !!priceQuote
      ? scheduleBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          isRecommended: queryString.recommended === "true",
          ancillaries,
          initialTime: dayjs().toISOString(),
        })
      : schedulePriceQuote({
          history,
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          ancillaries,
        });
  };

  const mergedPolicies = useMemo(
    () =>
      [
        ...(chosenProduct?.policies ?? []),
        ...(priceQuote?.checkInInstructions.policies ?? []),
      ].reduce((uniquePolicies, currentPolicy) => {
        if (
          !uniquePolicies.some(({ title }) => currentPolicy.title === title)
        ) {
          uniquePolicies.push(currentPolicy);
        }
        return uniquePolicies;
      }, [] as CheckInPolicy[]),
    [chosenProduct?.policies, priceQuote?.checkInInstructions.policies],
  );

  useEffect(() => {
    if (reservation) {
      history.push(PATH_BOOK_CONFIRMATION);
    }
  }, [reservation]);

  useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setDisableEditTravelerInfo(false);
    }
  }, [priceQuoteErrors, confirmationDetailsErrors]);

  // Re-lock the traveler step after a successful reshop / connection-retry
  // (CT1261T-419) re-quote (the error had unlocked it via the effect above).
  // Require a current quote: editing travelers clears it, and re-completing the
  // form must not relock the unsaved step without a fresh quote.
  useEffect(() => {
    if (priceQuote && showRequoteSuccessBanner && isFirstStepCompletedInCheckout) {
      setDisableEditTravelerInfo(true);
    }
  }, [priceQuote, showRequoteSuccessBanner, isFirstStepCompletedInCheckout]);

  useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
  }, []);

  const renderLogo = (
    <ButtonWrap
      className="logo"
      onClick={() => {
        history.push(PATH_HOME);
      }}
    >
      {clientContext.logo}
    </ButtonWrap>
  );

  const handleBookingInfoClick = useCallback(() => {
    setDetailsModalOpen(!detailsModalOpen);
  }, [detailsModalOpen]);

  return (
    <Box
      className={clsx("desktop-hotel-book-workflow-root", {
        "with-progress-bar": showCheckoutBreadcrumbs,
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      {detailsModalOpen && (
        <DesktopPopupModal
          open={detailsModalOpen}
          onClose={handleBookingInfoClick}
          className="desktop-hotel-book-details-modal"
        >
          <DesktopShop hideRightColumn hideProgressbar />
        </DesktopPopupModal>
      )}
      <CheckoutStepper
        steps={combinedBookingSteps}
        headerCopy={BOOK_BUTTON_TEXT}
        subHeaderCopy={REQUIRED_DETAILS_TEXT}
        logo={renderLogo}
        className={clsx("b2b", "combined-step")}
      />
      {/*
        Rendered after the stepper so its sticky placeholder sits below the
        stepper; earlier placement left a gap under the header at scroll top.
      */}
      <ReshopSuccessBanner />
      <ConnectionRetrySuccessBanner />
      <PriceChangeInlineMessage />
      {showCheckoutBreadcrumbs && (
        <Box className="hotel-book-progress-bar-wrapper">
          <HotelShopProgressBar
            useCustomizeStep={showCheckoutBreadcrumbsCustomizeStep}
            core={core}
          />
        </Box>
      )}
      <Box className="desktop-hotel-book-workflow-container">
        <Box className={clsx("checkout-template-column", "left")}>
          {isReadyToDisplayHotelSummaryDetails && (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "hotel",
              )}
            >
              <HotelBookSummaryPanel
                className="b2b"
                actions={
                  isAgentPortal ? (
                    <Typography
                      role="button"
                      variant="h6"
                      className="view-hotel-information"
                      onClick={handleBookingInfoClick}
                    >
                      {AGENT_DETAILS_MODAL_TEXT}
                    </Typography>
                  ) : null
                }
              />
            </Box>
          )}

          {isTravelWalletOfferExperiment &&
            !isCreditAndOfferStackingExperimentV1 && <TravelOfferSelection />}

          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "traveler",
            )}
          >
            <>
              <Box className={clsx("checkout-template-description")}>
                <Typography
                  variant="h2"
                  className={clsx("checkout-template-title")}
                >
                  {TRAVELER_INFO_TITLE}
                </Typography>
              </Box>
              {!isVesper && <Divider className="traveler-info-container-divider" />}
            </>
            <HotelBookPassengerSelection
              disabled={disableEditTravelerInfo}
              progress={travelerWorkflowStep}
              setProgress={setTravelerWorkflowStep}
              combinedStep
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              saveButtonClicked={saveButtonClicked}
              hiddenTravelerIds={[]}
            />
            {!isVesper && <Divider className="traveler-info-container-divider" />}
            <ContactInfoForm
              disabled={disableEditTravelerInfo}
              title={CONTACT_INFO_TITLE_NO_STEP}
              subtitle={CONTACT_INFO_SUBTITLE}
              contactInfo={contactInfo}
              onContactInfoChange={(info: IContactInfo) => {
                setContact(info);
                setContactInfo(info.email, info.phoneNumber);
                if (
                  info.email &&
                  info.phoneNumber &&
                  emailRegex.test(info.email) &&
                  phoneRegex.test(info.phoneNumber)
                ) {
                  trackEvent({
                    eventName: ADD_CONTACT_INFO,
                    properties: {},
                  });
                }
              }}
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              loading={customerDetailsLoading}
            />
            {!disableEditTravelerInfo && (
              <ActionButton
                className={clsx(
                  "traveler-select-workflow-button",
                  "traveler-step-cta",
                )}
                message="Save & continue"
                onClick={() => {
                  if (isFirstStepCompletedInCheckout) {
                    schedulePriceQuote({
                      history,
                      agentFee: isAgentPortal ? AGENT_FEE : 0,
                      pollQuoteOnly: true,
                      ancillaries,
                    });
                    setDisableEditTravelerInfo(true);
                    setShowErrors({
                      phone: false,
                      email: false,
                      travelerSelect: false,
                    });
                    trackEvent({
                      eventName: HOTEL_SAVE_AND_CONTINUE_CLICKED,
                      properties: {
                        ...getPromotionTrackingProperties(selectedLodging),
                        has_form_errors: false,
                      },
                    });
                  } else {
                    trackEvent({
                      eventName: HOTEL_SAVE_AND_CONTINUE_CLICKED,
                      properties: {
                        ...getPromotionTrackingProperties(selectedLodging),
                        has_form_errors: true,
                        traveler_error: !isTravelerStepComplete,
                        email_error:
                          !contactInfo || !emailRegex.test(contactInfo.email),
                        phone_error:
                          !contactInfo ||
                          !phoneRegex.test(contactInfo.phoneNumber),
                      },
                    });
                    setShowErrors({
                      phone:
                        !contactInfo ||
                        !phoneRegex.test(contactInfo.phoneNumber),
                      email:
                        !contactInfo || !emailRegex.test(contactInfo.email),
                      travelerSelect: !isTravelerStepComplete,
                    });
                    if (!saveButtonClicked) {
                      setSaveButtonClicked(true);
                    }
                  }
                }}
                defaultStyle="h4r-primary"
              />
            )}
            {disableEditTravelerInfo && (
              <ActionLink
                className={clsx("traveler-step-cta", "b2b")}
                content={
                  <Box className="edit-traveler-link">
                    <Typography variant="body1">
                      Edit traveler information
                    </Typography>
                    <Icon
                      aria-hidden
                      className="icon-end"
                      name={IconName.B2BEditPencil}
                      ariaLabel=""
                    />
                  </Box>
                }
                onClick={() => {
                  setPriceQuote({
                    priceQuote: null,
                    pricingWithAncillaries: null,
                    hotelAncillaryQuotes: null,
                    type: "hotel-checkout",
                  });
                  setDisableEditTravelerInfo(false);
                  setSelectedPaymentMethodId({
                    paymentMethodId: "",
                    accountId: undefined,
                  });
                  setSelectedRewardsAccountReferenceId(null);
                }}
              />
            )}
            <Box className={clsx("contact-info-helper-container")}>
              <Typography
                variant="body2"
                className={clsx("contact-info-helper-text")}
              >
                {CONTACT_INFO_HELPER_TEXT}
              </Typography>
            </Box>
          </Box>
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "credit",
            )}
          >
            <PaymentCard
              disabled={
                !isFirstStepCompletedInCheckout ||
                !disableEditTravelerInfo ||
                (!isCreditAndOfferStackingExperimentV1 &&
                  isTravelWalletPaymentOnly)
              }
            />
            {isTreesModalExperiment && (
              <TreesModal
                image={CDN_ASSETS.capone.trees.confirmation}
                header={TREES_MODAL_HEADER}
                title={TREES_MODAL_TITLE}
                onClose={() => setTreeModalOpen(false)}
                subtitle={TREES_MODAL_SUBTITLE}
                icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
                openModal={treeModalOpen}
                setOpenModal={setTreeModalOpen}
                modalLinkCopy={TREES_MODAL_CTA_TEXT}
                modalButtonCopyStartIcon={
                  <Icon className="trees-icon" name={IconName.TreesIcon} />
                }
                boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
              />
            )}
          </Box>
          {hasCfarAttached ? (
            <CfarCancellationPolicy />
          ) : cancellationPolicyInfo ? (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "cancellation-details",
              )}
            >
              <Typography variant="h4" component="h2">
                {cancellationPolicyInfo.primaryText}
              </Typography>
              <Typography variant="body2">
                {showFree24HourCancel &&
                chosenProduct?.cancellationPolicy.CancellationPolicy ===
                  CancellationPolicyEnum.NonRefundable
                  ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                  : cancellationPolicyInfo.secondaryText}
              </Typography>
            </Box>
          ) : null}
          {selectedLodging?.lodging?.checkInInstructions &&
            checkIn &&
            checkOut && (
              <Box
                className={clsx(
                  "checkout-template-card-content-container",
                  "check-in-instructions",
                )}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  className="hotel-shop-check-in-instructions-heading"
                >
                  {CHECK_IN_INSTRUCTIONS_TITLE}
                </Typography>
                <SanitizedHtml
                  as={Typography}
                  variant="body2"
                  html={formattedCheckIn(
                    checkIn,
                    selectedLodging.lodging.checkInInstructions?.checkInTime,
                  )}
                />
                <SanitizedHtml
                  as={Typography}
                  variant="body2"
                  html={formattedCheckOut(
                    checkOut,
                    selectedLodging.lodging.checkInInstructions?.checkOutTime,
                  )}
                />
              </Box>
            )}
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "need-to-know",
            )}
          >
            <HotelNeedToKnowPanel
              title={NEED_TO_KNOW_TITLE}
              subtitle={NEED_TO_KNOW_DETAILS}
              policies={mergedPolicies}
              fetchingPriceQuote={priceQuoteCallState === CallState.InProcess}
            />
          </Box>
        </Box>
        <Box className={clsx("checkout-template-column", "right")}>
          <Box className="checkout-template-column-right-wrapper">
            <Box
              className={clsx("hotel-book-right-content", "checkout-breakdown")}
            >
              <Box
                className={clsx(
                  "checkout-template-card-content-container",
                  "payment-break-down",
                )}
              >
                {!!roomInfoProduct && chosenProductIndex !== null && (
                  <RoomDetails
                    roomInfoProduct={roomInfoProduct}
                    productIndex={chosenProductIndex}
                    lodging={selectedLodging}
                    hideAmenities
                    hideAllAmenities
                    roomsCount={roomsCount}
                    hideRoomBedDescription
                    hideRoomCapacity
                    showAmenitiesModal
                  />
                )}
                <PriceBreakdown />
              </Box>

              {getHSPEarnOfferDescription(offerToApply) && (
                <EarnOfferBanner />
              )}

              <Box className="confirm-book-button-container">
                <Button
                  className="confirm-book-button"
                  disabled={!isBookingValid || !allStepsCompleted}
                  onClick={onBookHotel}
                >
                  {BOOK_BUTTON_TEXT}
                </Button>
              </Box>
            </Box>
            {showFree24HourCancel && <VoidWindowNotice />}
            {selectedLodging?.lodgingCollection ===
              LodgingCollectionEnum.Premier && (
              <PremierCollectionCheckoutBenefits />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
